import {
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { materialsApi } from '@texas/api/endpoints/metadata/materialsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { formatLabel } from '@texas/components/shared/composition/material/shared';

interface Props {
  materialId: number;
  selectedMaterialIds: number[];
  onAdd: (materialId: number) => void;
}

export function MaterialRecommendations({
  materialId,
  selectedMaterialIds,
  onAdd,
}: Props) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApiResource(
    materialsApi.recommendations,
  );

  useEffect(() => {
    refetch(materialId);
  }, [materialId, refetch]);

  if (!loading && !error && !data?.length) return null;

  return (
    <VStack align="stretch" spacing={2} mt={3}>
      <Text fontSize="sm" fontWeight="semibold">
        {t('configuration.materialRecommendations')}
      </Text>
      {loading ? (
        <Flex align="center" gap={2} role="status">
          <Spinner size="sm" />
          <Text fontSize="sm">
            {t('configuration.findingMaterialRecommendations')}
          </Text>
        </Flex>
      ) : error ? (
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color="entityDirectory.mutedText">
            {t('configuration.materialRecommendationsUnavailable')}
          </Text>
          <Button
            type="button"
            size="sm"
            variant="entityDirectorySecondary"
            onClick={() => refetch(materialId)}
          >
            {t('general.retry')}
          </Button>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {data?.map((group) => (
            <VStack
              key={group.term}
              align="stretch"
              spacing={2}
              p={3}
              borderWidth="1px"
              borderColor="entityDirectory.border"
            >
              <Text fontSize="xs" color="entityDirectory.mutedText">
                {group.term}
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {group.materials.map((material) => (
                  <Button
                    key={material.id}
                    type="button"
                    size="sm"
                    variant="entityDirectorySecondary"
                    height="auto"
                    maxW="100%"
                    minH={8}
                    py={1}
                    whiteSpace="normal"
                    isDisabled={selectedMaterialIds.includes(material.id)}
                    onClick={() => onAdd(material.id)}
                  >
                    {formatLabel(material)}
                  </Button>
                ))}
                {group.materials.length === 0 && (
                  <Text fontSize="sm" color="entityDirectory.mutedText">
                    {t('configuration.noMaterialRecommendations')}
                  </Text>
                )}
              </Flex>
            </VStack>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
}
